<template>
  <div class="so-map">
    <div ref="so-map" class="so-map__map" data-testid="so-map"></div>

    <div
      v-if="loading"
      class="so-map__overlay pa-5"
      role="alert"
      aria-live="polite"
      data-testid="so-map-loading"
    >
      <div class="ma-auto">
        <v-icon icon="loading spin" size="x-large"></v-icon>
        <p class="mt-5">Loading map</p>
      </div>
    </div>

    <div
      v-if="showError"
      class="so-map__overlay pa-5"
      role="alert"
      aria-live="polite"
      data-testid="so-map-error"
    >
      <div class="ma-auto">
        <v-icon icon="close-circle" size="x-large"></v-icon>
        <p class="mt-5">Could not load Google Maps</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { MapService } from "@/services/MapService"
  import { MapType, MapOptions, MapLatLng } from "@/types/Map"

  export default defineComponent({
    inheritAttrs: false,
    props: {
      location: {
        type: Object as PropType<MapLatLng | null>,
        default: null,
      },
      mapType: {
        type: String as PropType<MapType>,
        default: "roadmap",
      },
      zoom: {
        type: Number,
        default: 14,
      },
      hasControls: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        showError: false,
        loading: true,
        gmap: new MapService(),
      }
    },
    computed: {
      getMapOptions(): MapOptions {
        return {
          ...(this.location ? { center: this.location } : {}),
          mapType: this.mapType,
          zoom: this.zoom,
          hasControls: this.hasControls,
        }
      },
    },
    watch: {
      location: {
        deep: true,
        handler(newLocation) {
          this.gmap.setMarker(newLocation)
        },
      },
    },
    async mounted() {
      await this.initMap()
      if (this.location) {
        this.gmap.setMarker(this.location)
      }
      this.loading = false
    },
    unmounted() {
      if (this.gmap) {
        this.gmap.destroyMap()
      }
    },
    methods: {
      async initMap() {
        const mapElement = this.$refs["so-map"] as HTMLElement
        try {
          await this.gmap.initMap(mapElement, this.getMapOptions)
        } catch (e) {
          this.showError = true
          console.error(e)
        }
      },
    },
  })
</script>
<style>
  .so-map {
    position: relative;
    background-color: rgb(var(--v-theme-map-background));
  }

  .so-map,
  .so-map__map {
    width: 100%;
    height: 100%;
  }

  .so-map__overlay {
    position: absolute;
    background-color: rgb(var(--v-theme-map-background));
    text-align: center;
    display: flex;
    inset: 0;
    z-index: 1;
  }
</style>
